import { Box } from '@zillow/constellation';
import React from 'react';

import type { AuthorPageContentProps } from '../../util/buildPrimaryAuthorPage';

import AuthorHeader from './AuthorHeader';
import AuthorMainContent from './AuthorMainContent';
import type { AuthorSocialLinks } from './AuthorPage.types';
import AuthorSidebar from './AuthorSidebar';

/**
 * The maximum width for the outer AuthorPage container.
 */
const AUTHOR_PAGE_MAX_WIDTH = '2560px';

/**
 * The maximum width for the main content row inside AuthorPage.
 */
export const AUTHOR_PAGE_CONTENT_MAX_WIDTH = '1280px';

/**
 * AuthorPage component for displaying author details and posts.
 *
 * @param sidebarTop - The CSS top value for the sticky sidebar (e.g., '$spacing.xl', '80px').
 */
const AuthorPage: React.FC<
    Omit<AuthorPageContentProps, 'total'> & { sidebarTop?: string }
> = ({ author, posts, pages, initialPage, sidebarTop }) => {
    // Type guard for socialLinks
    const socialLinks: AuthorSocialLinks | undefined =
        typeof author.socialLinks === 'object' && author.socialLinks !== null
            ? (author.socialLinks as AuthorSocialLinks)
            : undefined;

    return (
        <Box
            sx={{
                maxWidth: AUTHOR_PAGE_MAX_WIDTH,
                margin: '0 auto',
            }}
        >
            {/* Header */}
            <AuthorHeader author={author} socialLinks={socialLinks} />

            {/* Content Row */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    zIndex: 1,
                    paddingLeft: '$spacing.md',
                    paddingRight: '$spacing.md',
                    gap: '$spacing.xxl',
                    margin: '0 auto',
                    justifyContent: 'center',
                    maxWidth: AUTHOR_PAGE_CONTENT_MAX_WIDTH,
                    '@media $lg': {
                        flexDirection: 'row',
                        paddingLeft: '$spacing.xl',
                        paddingRight: '$spacing.xl',
                    },
                    '@media $xl': {
                        paddingLeft: '$spacing.xxl',
                        paddingRight: '$spacing.xxl',
                    },
                }}
            >
                {/* Sidebar - hidden below desktop */}
                <AuthorSidebar author={author} sidebarTop={sidebarTop} />

                {/* Main Content */}
                <AuthorMainContent
                    author={author}
                    posts={posts}
                    pages={pages}
                    initialPage={initialPage}
                />
            </Box>
        </Box>
    );
};

export default AuthorPage;
